import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, AlertCircle, Upload as UploadIcon, Mail } from 'lucide-react';
import axios from 'axios';
import OrderProgressTracker from '../components/OrderProgressTracker';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * OrderStatusPage
 * Public order tracking page - customer visits /order/:orderId to see progress.
 * Order IDs are unguessable UUIDs, matching the existing PaymentSuccess pattern.
 */

const OrderStatusPage = () => {
  const { orderId } = useParams();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/api/order/${orderId}`);
        if (mounted && res.data?.order) {
          setOrder(res.data.order);
        } else if (mounted) {
          setError('Order not found');
        }
      } catch (e) {
        if (mounted) setError('We couldn\'t find that order. Please double-check your order ID.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, [orderId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white pt-32 pb-16 px-4 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-amber-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading your project status...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-black text-white pt-32 pb-16 px-4">
        <div className="max-w-xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/10 border border-red-500/30 mb-6">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-3xl font-bold mb-3">Order not found</h1>
          <p className="text-gray-400 mb-8">{error || 'This order link is invalid or has expired.'}</p>
          <Link
            to="/contact"
            className="inline-flex items-center px-6 py-3 rounded-full border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 transition-colors"
          >
            <Mail size={16} className="mr-2" />
            Contact us for help
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pt-28 md:pt-36 pb-16">
      <div className="container mx-auto px-4 max-w-3xl">
        {/* Header */}
        <div className="text-center mb-10" data-testid="order-status-header">
          <p className="uppercase tracking-[0.3em] text-xs text-amber-400 mb-3">Your Project</p>
          <h1 className="text-3xl md:text-4xl font-bold text-white">
            {order.name ? `Hi ${order.name.split(' ')[0]},` : 'Hi there,'}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">
              here's your progress
            </span>
          </h1>
        </div>

        {/* Order summary card */}
        <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 md:p-8 mb-6" data-testid="order-details-card">
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500 uppercase tracking-wider text-[11px] mb-1">Order ID</p>
              <p className="font-mono text-amber-400">{order.order_id}</p>
            </div>
            <div>
              <p className="text-gray-500 uppercase tracking-wider text-[11px] mb-1">Package</p>
              <p className="text-white font-medium">{order.package_type || '—'}</p>
            </div>
            {Number(order.total_price) > 0 && (
              <>
                {(order.addons || []).length > 0 && (
                  <div className="sm:col-span-2" data-testid="order-status-addons">
                    <p className="text-gray-500 uppercase tracking-wider text-[11px] mb-1">Add-ons</p>
                    <p className="text-white font-medium">
                      {order.addons.map((a) => `${a.name}${a.quantity > 1 ? ` (${a.quantity})` : ''} +£${Number(a.price).toFixed(2)}`).join(' · ')}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-gray-500 uppercase tracking-wider text-[11px] mb-1">Total Order Price</p>
                  <p className="text-white font-medium">£{order.total_price}</p>
                </div>
                <div>
                  <p className="text-gray-500 uppercase tracking-wider text-[11px] mb-1">Deposit Paid</p>
                  <p className="text-green-400 font-medium">£{order.deposit_amount}</p>
                </div>
                <div>
                  <p className="text-gray-500 uppercase tracking-wider text-[11px] mb-1">Remaining Balance</p>
                  <p className="text-white font-medium" data-testid="order-status-remaining">£{Math.max(Number(order.total_price) - Number(order.deposit_amount), 0).toFixed(2)}</p>
                </div>
              </>
            )}
          </div>

          {order.delivery_address && (
            <div className="mt-6 pt-6 border-t border-white/10" data-testid="order-status-delivery-address">
              <p className="text-gray-500 uppercase tracking-wider text-[11px] mb-2">USB Delivery Address</p>
              <div className="text-sm text-gray-200 space-y-0.5">
                <div className="text-white font-medium">{order.delivery_address.name}</div>
                {order.delivery_address.line1 && <div>{order.delivery_address.line1}</div>}
                {order.delivery_address.line2 && <div>{order.delivery_address.line2}</div>}
                <div>
                  {[order.delivery_address.city, order.delivery_address.state, order.delivery_address.postal_code].filter(Boolean).join(', ')}
                </div>
                {order.delivery_address.country && <div className="text-gray-400">{order.delivery_address.country}</div>}
              </div>
            </div>
          )}
        </div>

        {/* Progress Tracker */}
        <div className="bg-gradient-to-br from-white/[0.04] to-white/[0.01] border border-white/10 rounded-2xl p-6 md:p-8 mb-6">
          <OrderProgressTracker stage={order.production_stage || 0} />
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to={`/upload/${order.order_id}`}
            data-testid="order-status-upload-cta"
            className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold hover:from-amber-600 hover:to-amber-700 transition-all"
          >
            <UploadIcon size={16} className="mr-2" />
            Upload / manage media
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-6 py-3 rounded-full border border-white/15 text-white hover:border-amber-500/50 hover:text-amber-400 transition-colors"
          >
            <Mail size={16} className="mr-2" />
            Contact us
          </Link>
        </div>

        <p className="text-center text-xs text-gray-500 mt-10">
          Keep this page bookmarked to check on your project any time. We'll email you the moment your film is ready.
        </p>
      </div>
    </div>
  );
};

export default OrderStatusPage;
